/**
 * Fragment de routes du paquet v5-p01-architecture-ecrans (vague V5). Composants chargés à la demande (règle de
 * chargement du registre : aucun code d'écran dans le paquet principal, aucun effet au chargement).
 * Routes du socle remplacées : `/decisions` → projet-decisions-architecture (la portée Outil d'un projet a sa propre
 * route `/decisions/outil` ; `/plateforme/decisions` reste l'écran de la plateforme), `/architecture` →
 * projet-architecture, `/architecture/versions/:releaseId/composant` → projet-arc-composant-fiche. La fiche de dépendance
 * (`…/dependance`) reste celle du socle. Les dialogues sont des écrans routés : l'écran d'arrivée vérifie lui-même l'accès.
 */
import { lazy, Suspense, type ComponentType, type JSX } from 'react'

import type { Session } from '../../auth/session'
import type { ContexteRoutes, FragmentRoutes } from './registre'

const ProjetDecisionsArchitecture = lazy(() => import('../ecrans/v5/architecture/ProjetDecisionsArchitecture'))
const ProjetArcDecisionsOutil = lazy(() => import('../ecrans/v5/architecture/ProjetArcDecisionsOutil'))
const ProjetArcDecisionOutilFiche = lazy(() => import('../ecrans/v5/architecture/ProjetArcDecisionOutilFiche'))
const ProjetDecisionArchitectureFiche = lazy(() => import('../ecrans/v5/architecture/ProjetDecisionArchitectureFiche'))
const ProjetArcDecisionEditeur = lazy(() => import('../ecrans/v5/architecture/ProjetArcDecisionEditeur'))
const DialogueCreerAdr = lazy(() => import('../ecrans/v5/architecture/DialogueCreerAdr'))
const DialogueRevoirAdr = lazy(() => import('../ecrans/v5/architecture/DialogueRevoirAdr'))
const DialogueDeciderArchitecture = lazy(() => import('../ecrans/v5/architecture/DialogueDeciderArchitecture'))
const DialogueRemplacerAdr = lazy(() => import('../ecrans/v5/architecture/DialogueRemplacerAdr'))
const DialogueArcAnnulerAdr = lazy(() => import('../ecrans/v5/architecture/DialogueArcAnnulerAdr'))
const ProjetArchitecture = lazy(() => import('../ecrans/v5/architecture/ProjetArchitecture'))
const ProjetArcArchitectureVersionFiche = lazy(() => import('../ecrans/v5/architecture/ProjetArcArchitectureVersionFiche'))
const ProjetArcComposantFiche = lazy(() => import('../ecrans/v5/architecture/ProjetArcComposantFiche'))
const DialogueComparerBaseline = lazy(() => import('../ecrans/v5/architecture/DialogueComparerBaseline'))
const DialogueArcAjouterReprise = lazy(() => import('../ecrans/v5/architecture/DialogueArcAjouterReprise'))
const DialogueArcVerifierReprise = lazy(() => import('../ecrans/v5/architecture/DialogueArcVerifierReprise'))
const DialogueSolliciterDroit = lazy(() => import('../ecrans/v5/architecture/DialogueSolliciterDroit'))

function ecran(Composant: ComponentType<{ session: Session }>) {
  return ({ session, t }: ContexteRoutes): JSX.Element => (
    <Suspense fallback={<p role="status">{t('v10.primitives.assemblage.chargement')}</p>}>
      <Composant session={session} />
    </Suspense>
  )
}

export const fragment: FragmentRoutes = {
  zone: 'v5-architecture',
  routes: [
    { chemin: '/decisions', element: ecran(ProjetDecisionsArchitecture), remplace: true },
    { chemin: '/decisions/nouvelle', element: ecran(DialogueCreerAdr) },
    { chemin: '/decisions/outil', element: ecran(ProjetArcDecisionsOutil) },
    { chemin: '/decisions/outil/:adrId', element: ecran(ProjetArcDecisionOutilFiche) },
    { chemin: '/decisions/:adrId', element: ecran(ProjetDecisionArchitectureFiche) },
    { chemin: '/decisions/:adrId/preparer', element: ecran(ProjetArcDecisionEditeur) },
    { chemin: '/decisions/:adrId/revoir', element: ecran(DialogueRevoirAdr) },
    { chemin: '/decisions/:adrId/decider', element: ecran(DialogueDeciderArchitecture) },
    { chemin: '/decisions/:adrId/remplacer', element: ecran(DialogueRemplacerAdr) },
    { chemin: '/decisions/:adrId/annuler', element: ecran(DialogueArcAnnulerAdr) },
    { chemin: '/architecture', element: ecran(ProjetArchitecture), remplace: true },
    { chemin: '/architecture/versions/:releaseId', element: ecran(ProjetArcArchitectureVersionFiche) },
    { chemin: '/architecture/versions/:releaseId/composant', element: ecran(ProjetArcComposantFiche), remplace: true },
    { chemin: '/technologies/comparer-heritage', element: ecran(DialogueComparerBaseline) },
    { chemin: '/technologies/changements/:changeId/reprise', element: ecran(DialogueArcAjouterReprise) },
    { chemin: '/technologies/changements/:changeId/verifier', element: ecran(DialogueArcVerifierReprise) },
    { chemin: '/acces-projet/solliciter-droit', element: ecran(DialogueSolliciterDroit) },
  ],
}
